import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ChevronLeft, ChevronRight, Copy, Gem, RefreshCw, ShieldCheck, Truck, Instagram } from "lucide-react";
import { toast } from "sonner";
import ProductCard from "@/components/site/ProductCard";
import { useShop } from "@/context/ShopContext";
import { BUDGETS, CATEGORIES, GUIDES, IMG, INSTAGRAM, OCCASIONS, PROMOS, inr } from "@/lib/content";

export const SectionHead = ({ eyebrow, title, action }) => (
  <div className="mb-10 flex flex-col gap-4 sm:mb-14 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h2 className="mt-3 max-w-2xl font-serif-display text-4xl font-semibold leading-[1.05] sm:text-5xl">{title}</h2>
    </div>
    {action}
  </div>
);

export const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden">
    <div className="kt-container grid items-center gap-10 py-10 sm:py-16 lg:grid-cols-12 lg:py-20">
      <div className="rise lg:col-span-6">
        <p className="eyebrow">The Navratri &amp; Festive Edit 2026</p>
        <h1 className="mt-5 font-serif-display text-5xl font-medium leading-[1.02] tracking-tight text-[#1C1917] sm:text-6xl lg:text-7xl">
          Everyday Elegance, <em className="font-medium text-[#701A24]">Beautifully Indian</em>
        </h1>
        <p className="mt-6 max-w-lg text-base leading-relaxed text-[#57534E] sm:text-lg">Discover breathable cotton kurtis, graceful Anarkali suits, and festive styles made for every memorable moment.</p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/collections/new-arrivals" data-testid="hero-shop-new-arrivals-button" className="btn-primary">Shop New Arrivals <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/collections/festive-wear" data-testid="hero-explore-festive-button" className="btn-outline">Explore Festive Wear</Link>
        </div>
        <div className="mt-10 flex gap-8 text-sm text-[#57534E]">
          <span><b className="block font-serif-display text-2xl text-[#1B4332]">₹159+</b>styles from</span>
          <span><b className="block font-serif-display text-2xl text-[#1B4332]">XS–XXL</b>inclusive sizing</span>
          <span><b className="block font-serif-display text-2xl text-[#1B4332]">7-day</b>size exchange</span>
        </div>
      </div>
      <div className="relative lg:col-span-6">
        <div className="grain relative aspect-[4/5] overflow-hidden rounded-[2.5rem] rounded-tl-[9rem] bg-[#EAE0D5] sm:aspect-[5/5.4]">
          <img src={IMG.hero} alt="Woman in the Kurtaani wine embroidered Anarkali kurta set with dupatta" className="h-full w-full object-cover object-top" fetchPriority="high" />
        </div>
        <div className="absolute -bottom-6 -left-4 hidden w-44 overflow-hidden rounded-3xl border-[6px] border-[#FAF7F2] shadow-xl sm:block lg:-left-12">
          <img src={IMG.white} alt="Close-up of Kutch embroidery on the Kurtaani black kurta" className="aspect-[3/4] w-full object-cover" />
        </div>
        <div className="absolute right-4 top-6 rounded-2xl bg-[#FAF7F2]/90 px-4 py-3 shadow-lg backdrop-blur-md">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#B85D43]">Now trending</p>
          <p className="font-serif-display text-lg">Navratri Edit</p>
        </div>
      </div>
    </div>
  </section>
);

export const FirstOrderBanner = () => (
  <section className="kt-container">
    <div data-testid="first-order-banner" className="grain relative flex flex-col items-start gap-5 overflow-hidden rounded-3xl bg-[#701A24] px-6 py-8 text-[#FAF7F2] sm:flex-row sm:items-center sm:justify-between sm:px-12">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[#D99B8E]">Welcome offer</p>
        <p className="mt-2 font-serif-display text-3xl sm:text-4xl">Flat 20% OFF on Your First Order</p>
        <p className="mt-1 text-sm text-[#EAE0D5]/80">Use code <b className="tracking-widest text-white">FIRST20</b> at checkout.</p>
      </div>
      <Link to="/collections/all" data-testid="shop-the-collection-button" className="btn-light">Shop the Collection <ArrowUpRight className="h-4 w-4" /></Link>
    </div>
  </section>
);

export const NavratriBanner = () => (
  <section className="kt-container pt-16 sm:pt-24">
    <Link to="/navratri-couple-edit" data-testid="navratri-edit-banner" className="group grid overflow-hidden rounded-3xl bg-[#1B4332] text-[#FAF7F2] sm:grid-cols-2">
      <div className="flex flex-col justify-center p-8 sm:p-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#F2C230]">New · The Navratri Couple Edit</p>
        <p className="mt-3 font-serif-display text-4xl leading-tight sm:text-5xl">Twirl together, match in spirit</p>
        <p className="mt-3 text-sm text-[#EAE0D5]/80">Garba-ready couple looks built around our chaniya cholis.</p>
        <span className="btn-light mt-6 w-fit">Explore Couple Looks <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
      </div>
      <div className="grid grid-cols-2">
        <img src="/catalog/p02.webp" alt="Navratri couple in black chaniya choli and kurta" loading="lazy" className="aspect-[3/4] h-full w-full object-cover object-top" />
        <img src="/catalog/p10.webp" alt="Navratri couple in white polka chaniya choli and kurta" loading="lazy" className="aspect-[3/4] h-full w-full object-cover object-top" />
      </div>
    </Link>
  </section>
);

export const HeroPicks = ({ products }) => {
  const { addToCart } = useShop();
  if (products.length < 2) return null;
  return (
    <section data-testid="hero-picks-section" className="kt-container pb-16 sm:pb-20">
      <SectionHead eyebrow="Navratri hero picks" title="This season's showstoppers" action={<Link to="/combo" className="btn-outline">Get both for ₹329 <ArrowRight className="h-4 w-4" /></Link>} />
      <div className="grid gap-6 md:grid-cols-2">
        {products.map((p, i) => (
          <article key={p.slug} data-testid={`hero-pick-${p.slug}`} className="group relative overflow-hidden rounded-[2rem] bg-[#1C1917]">
            <Link to={`/products/${p.slug}`}>
              <img src={p.images[0]} alt={p.alt} className="aspect-[4/5] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/90 via-transparent to-transparent" />
            </Link>
            <span className="absolute left-5 top-5 rounded-full bg-[#FAF7F2] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#701A24]">Hero pick 0{i + 1}</span>
            <div className="absolute inset-x-0 bottom-0 p-6 text-[#FAF7F2] sm:p-8">
              <h3 className="font-serif-display text-3xl font-semibold leading-tight sm:text-4xl">{p.name}</h3>
              <p className="mt-2 flex items-baseline gap-2"><span className="text-2xl font-semibold">{inr(p.price)}</span><span className="text-sm line-through opacity-60">{inr(p.mrp)}</span><span className="text-sm font-semibold text-[#F2C230]">{p.discount_pct}% off</span></p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to={`/products/${p.slug}`} data-testid={`hero-pick-shop-${p.slug}`} className="btn-light">Shop Now <ArrowRight className="h-4 w-4" /></Link>
                <button data-testid={`hero-pick-add-${p.slug}`} onClick={() => addToCart(p, p.stock.M > 0 ? "M" : p.sizes.find((s) => p.stock[s] > 0), p.colors[0].name)} className="inline-flex items-center gap-2 rounded-full border border-[#FAF7F2]/60 px-6 py-3.5 text-sm font-semibold transition-colors hover:bg-[#FAF7F2] hover:text-[#701A24]">Quick Add · M</button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export const Categories = () => (
  <section data-testid="categories-section" className="kt-container py-16 sm:py-24">
    <SectionHead eyebrow="Shop by category" title="Find your silhouette" />
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {CATEGORIES.map((c, i) => (
        <Link key={c.slug} to={`/collections/${c.slug}`} data-testid={`category-card-${c.slug}`} className={`group relative self-start overflow-hidden rounded-[2rem] ${i % 2 ? "lg:mt-12" : ""}`}>
          <img src={c.img} alt={`${c.name} for women by Kurtaani`} loading="lazy" className="aspect-[3/4] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/85 via-[#1C1917]/10 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-[#FAF7F2]">
            <h3 className="font-serif-display text-3xl font-semibold">{c.name}</h3>
            <p className="mt-1 text-sm text-[#EAE0D5]/90">{c.desc}</p>
            <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FAF7F2] px-5 py-2.5 text-xs font-semibold text-[#701A24] transition-transform group-hover:translate-x-1">Shop Now <ArrowRight className="h-3.5 w-3.5" /></span>
          </div>
        </Link>
      ))}
    </div>
  </section>
);

export const ProductGrid = ({ products, testid }) => (
  <div data-testid={testid} className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
    {products.map((p) => <ProductCard key={p.slug} p={p} />)}
  </div>
);

export const Carousel = ({ products }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: "smooth" });
  return (
    <div className="relative">
      <div ref={ref} data-testid="new-arrivals-carousel" className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:gap-6">
        {products.map((p) => <div key={p.slug} className="w-[62%] shrink-0 snap-start sm:w-[38%] lg:w-[23.5%]"><ProductCard p={p} /></div>)}
      </div>
      <div className="mt-6 flex gap-2">
        <button data-testid="carousel-prev-button" onClick={() => scroll(-1)} aria-label="Previous" className="rounded-full border border-[#1C1917] p-3 transition-colors hover:bg-[#1C1917] hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
        <button data-testid="carousel-next-button" onClick={() => scroll(1)} aria-label="Next" className="rounded-full border border-[#1C1917] p-3 transition-colors hover:bg-[#1C1917] hover:text-white"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

export const Occasions = () => (
  <section data-testid="occasions-section" className="bg-[#F4EFEA] py-16 sm:py-24">
    <div className="kt-container pb-10">
      <SectionHead eyebrow="Shop by occasion" title="Dressed for every chapter of your day" />
      <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3">
        {OCCASIONS.map((o, i) => (
          <Link key={o.key} to={`/collections/all?occasions=${o.key}`} data-testid={`occasion-card-${o.key}`} className={`group relative overflow-hidden rounded-3xl ${i % 3 === 1 ? "lg:translate-y-10" : ""}`}>
            <img src={o.img} alt={`${o.name} ethnic outfits for women`} loading="lazy" className="aspect-[4/5] h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/75 to-transparent" />
            <p className="absolute bottom-4 left-4 right-4 flex items-end justify-between font-serif-display text-xl leading-tight text-white sm:text-2xl">{o.name} <ArrowUpRight className="h-5 w-5 shrink-0 transition-transform group-hover:-translate-y-1" /></p>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export const Budgets = () => (
  <section data-testid="budget-section" className="kt-container py-16 sm:py-24">
    <SectionHead eyebrow="Shop by budget" title="Beautiful, at every price point" />
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      {BUDGETS.map((b, i) => (
        <Link key={b.label} to={`/collections/all?${b.max ? `max_price=${b.max}` : `min_price=${b.min}`}${b.extra || ""}`} data-testid={`budget-option-${i}`} className={`group flex flex-col justify-between rounded-3xl border p-6 transition-colors duration-300 sm:p-8 ${i === 3 ? "border-[#1B4332] bg-[#1B4332] text-[#FAF7F2]" : "border-[#E7E2DB] bg-white hover:border-[#B85D43] hover:bg-[#F4E4DE]"}`}>
          <span className={`text-xs uppercase tracking-[0.2em] ${i === 3 ? "text-[#D99B8E]" : "text-[#B85D43]"}`}>{b.sub}</span>
          <span className="mt-10 flex items-end justify-between font-serif-display text-3xl font-semibold leading-none sm:text-4xl">{b.label}<ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      ))}
    </div>
  </section>
);

const copyCode = (c) => { navigator.clipboard?.writeText(c); toast.success(`Code ${c} copied`); };

export const Promos = () => (
  <section data-testid="promos-section" className="kt-container grid gap-4 sm:grid-cols-2">
    {PROMOS.map((p) => (
      <div key={p.title} className={`flex flex-col justify-between gap-6 rounded-3xl p-7 sm:p-9 ${p.tone}`}>
        <div><p className="font-serif-display text-3xl font-semibold leading-tight">{p.title}</p><p className="mt-2 text-sm opacity-80">{p.text}</p></div>
        {p.code ? (
          <button data-testid={`promo-copy-${p.code}`} onClick={() => copyCode(p.code)} className="flex w-fit items-center gap-3 rounded-full border border-dashed border-current px-5 py-2.5 text-sm font-semibold tracking-[0.2em]">{p.code} <Copy className="h-4 w-4" /></button>
        ) : p.link ? <Link to={p.link} data-testid="promo-build-combo-button" className="btn-primary w-fit">Build Your Combo <ArrowRight className="h-4 w-4" /></Link>
        : <span className="text-xs uppercase tracking-[0.2em] opacity-70">Applied automatically at checkout</span>}
      </div>
    ))}
  </section>
);

export const Inspiration = () => (
  <section data-testid="inspiration-section" className="kt-container py-16 sm:py-24">
    <SectionHead eyebrow="The style journal" title="Style inspiration, straight from our studio" />
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
      {GUIDES.map((g, i) => (
        <article key={g.slug} className={`group ${i % 2 ? "lg:mt-16" : ""}`}>
          <Link to={`/style/${g.slug}`} className="block overflow-hidden rounded-3xl"><img src={g.img} alt={g.title} loading="lazy" className="aspect-[4/5] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" /></Link>
          <p className="mt-5 text-[11px] uppercase tracking-[0.2em] text-[#78716C]">Guide 0{i + 1}</p>
          <h3 className="mt-2 font-serif-display text-2xl font-semibold leading-snug">{g.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-[#57534E]">{g.desc}</p>
          <Link to={`/style/${g.slug}`} data-testid={`read-guide-${g.slug}`} className="mt-4 inline-flex items-center gap-2 border-b border-[#701A24] pb-0.5 text-sm font-semibold text-[#701A24]">Read Style Guide <ArrowRight className="h-3.5 w-3.5" /></Link>
        </article>
      ))}
    </div>
  </section>
);

const TRUST = [[Gem, "Premium Fabrics", "Soft cottons, georgettes & silk blends, quality checked."], [RefreshCw, "Easy Size Exchange", "7-day hassle-free exchange on every order."], [ShieldCheck, "Secure Payments", "Pay securely via any UPI app – GPay, PhonePe, Paytm, BHIM."], [Truck, "Fast Delivery Across India", "Dispatched in 24–48 hours, free shipping."]];

export const Trust = () => (
  <section data-testid="trust-section" className="border-y border-[#E7E2DB] bg-white">
    <div className="kt-container grid grid-cols-2 gap-8 py-12 lg:grid-cols-4">
      {TRUST.map(([I, t, d]) => (
        <div key={t} className="flex flex-col items-start gap-3 sm:flex-row">
          <span className="rounded-full bg-[#F4E4DE] p-3 text-[#701A24]"><I className="h-5 w-5" /></span>
          <div><p className="font-serif-display text-xl font-semibold">{t}</p><p className="mt-1 text-xs leading-relaxed text-[#78716C] sm:text-sm">{d}</p></div>
        </div>
      ))}
    </div>
  </section>
);

export const InstagramGrid = () => (
  <section data-testid="instagram-section" className="kt-container py-16 sm:py-24">
    <div className="mb-10 text-center">
      <p className="eyebrow">#KurtaaniWoman</p>
      <h2 className="mt-3 font-serif-display text-4xl font-semibold sm:text-5xl">Style It. Share It. Tag @kurtaani</h2>
    </div>
    <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:grid-cols-6">
      {INSTAGRAM.map((src, i) => (
        <a key={i} href="https://instagram.com/kurtaani" target="_blank" rel="noreferrer" className="group relative overflow-hidden rounded-2xl">
          <img src={src} alt="Kurtaani style shared on Instagram" loading="lazy" className="aspect-square w-full object-cover object-top transition-transform duration-500 group-hover:scale-110" />
          <span className="absolute inset-0 flex items-center justify-center bg-[#701A24]/0 text-white opacity-0 transition-all group-hover:bg-[#701A24]/40 group-hover:opacity-100"><Instagram className="h-6 w-6" /></span>
        </a>
      ))}
    </div>
    <div className="mt-10 text-center">
      <a href="https://instagram.com/kurtaani" target="_blank" rel="noreferrer" data-testid="follow-instagram-button" className="btn-outline"><Instagram className="h-4 w-4" /> Follow Kurtaani on Instagram</a>
    </div>
  </section>
);
